import './library-page.web.css';

import { useRouter } from 'expo-router';
import { createElement, useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { useSearchParams } from 'react-router-dom';

import { DocumentTitle } from '@/components/layout/DocumentTitle';
import { Text } from '@/components/ui';
import { cn } from '@/lib/design-system/cn';
import { isOnline } from '@/lib/connectivity';
import { fetchLibrary, libraryEmptyMessage } from '@/lib/library/fetch-library';
import { getMemberDisplayMap } from '@/lib/library/fetch-library-members';
import { restoreTrashedPage } from '@/lib/library/restore-page';
import {
  LIBRARY_FILTER_LABELS,
  LIBRARY_FILTERS,
  parseLibraryFilter,
  type LibraryFilter,
  type LibraryRow,
} from '@/lib/library/types';

import { LibraryTable } from './LibraryTable.web';

export function LibraryPage() {
  const router = useRouter();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = parseLibraryFilter(searchParams.get('filter'));

  const [rows, setRows] = useState<LibraryRow[]>([]);
  const [memberNames, setMemberNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const offline = !isOnline();

  const load = useCallback(async (activeFilter: LibraryFilter) => {
    setLoading(true);
    setError(null);
    try {
      const [libraryRows, names] = await Promise.all([
        fetchLibrary(activeFilter),
        getMemberDisplayMap(),
      ]);
      setRows(libraryRows);
      setMemberNames(names);
    } catch (err) {
      setRows([]);
      setError(err instanceof Error ? err.message : 'Failed to load library');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOnline()) {
      setLoading(false);
      setRows([]);
      setError(null);
      return;
    }
    void load(filter);
  }, [filter, load]);

  function setFilter(next: LibraryFilter): void {
    if (next === 'recents') {
      setSearchParams({}, { replace: false });
      return;
    }
    setSearchParams({ filter: next }, { replace: false });
  }

  function openRow(row: LibraryRow): void {
    if (row.type === 'database') {
      router.push(`/database/${row.id}`);
    } else {
      router.push(`/page/${row.id}`);
    }
  }

  async function onRestore(row: LibraryRow): Promise<void> {
    const confirmed = window.confirm(`Restore "${row.title}"?`);
    if (!confirmed) {
      return;
    }
    setRestoringId(row.id);
    try {
      await restoreTrashedPage(row);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Restore failed');
    } finally {
      setRestoringId(null);
    }
  }

  const emptyMessage = libraryEmptyMessage(filter);
  const showEmpty = !loading && !error && rows.length === 0;

  return (
    <>
      <DocumentTitle title="Library" />
      <View className="flex w-full flex-1 flex-col items-stretch bg-surface px-page py-page-y">
        <Text variant="h1">Library</Text>

        <View
          className="cs-library-tabs mt-4 w-full flex-row flex-wrap items-center justify-start gap-1.5"
          role="tablist"
          aria-label="Library filters"
        >
          {LIBRARY_FILTERS.map((tab) => {
            const selected = tab === filter;
            return createElement(
              'button',
              {
                key: tab,
                type: 'button',
                role: 'tab',
                'aria-selected': selected,
                className: cn(
                  'cs-btn cs-btn-sm font-medium',
                  selected ? 'cs-btn-primary' : 'cs-btn-secondary',
                ),
                onClick: () => setFilter(tab),
              },
              LIBRARY_FILTER_LABELS[tab],
            );
          })}
        </View>

        {offline ? (
          <Text variant="body-sm" className="mt-6 text-left text-fg-muted">
            Library requires an online connection.
          </Text>
        ) : loading ? (
          <View className="mt-8 items-start">
            <ActivityIndicator />
          </View>
        ) : error ? (
          <Text variant="body-sm" className="mt-6 text-left text-danger">
            {error}
          </Text>
        ) : showEmpty ? (
          <Text variant="body-sm" className="mt-6 text-left text-fg-muted">
            {emptyMessage}
          </Text>
        ) : (
          <LibraryTable
            rows={rows}
            filter={filter}
            memberNames={memberNames}
            loading={false}
            restoringId={restoringId}
            onOpenRow={openRow}
            onRestore={(row) => void onRestore(row)}
          />
        )}
      </View>
    </>
  );
}
